import { useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Portal,
  Text,
  Flex,
  Input,
  Link,
  useToast,
} from '@chakra-ui/react';
import {
  EditIcon,
  CopyIcon,
  DeleteIcon,
  WarningTwoIcon,
} from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { useDuplicateTemplate } from './hooks/useDuplicateTemplate';
import {
  useDeletePassTemplateMutation,
  useArchivePassTemplateMutation,
  useLazyGetPassTemplateQuery,
} from '../../../store/redux/api/templateApi';
import CustomToast from '../../../common/CustomToast';
import ConfirmModal from '../../../common/ConfirmModal';
import { useIsReadOnly } from '../../../hooks/useIsReadOnly';

interface TemplateCardMenuProps {
  previewData: {
    uuid: string;
    templateName?: string;
    editUrl: string;
    status?: string;
    [key: string]: unknown;
  };
}

/**
 * Kebab "…" menu on a template card. Edit + Duplicate are always available.
 * Delete varies by status:
 *  - Draft   → destroy the record via DELETE /pass-templates/:id
 *  - Published (or any non-draft) → archive via POST /pass-templates/:id/archive
 *    (template is hidden; issued passes are nullified in the background)
 */
const TemplateCardMenu = ({ previewData }: TemplateCardMenuProps) => {
  const toast = useToast();
  const isDraft = previewData.status === 'draft';
  const isReadOnly = useIsReadOnly();

  const { handleDuplicate, loading } = useDuplicateTemplate(previewData);
  const [deletePassTemplate, { isLoading: isDeleting }] =
    useDeletePassTemplateMutation();
  const [archivePassTemplate, { isLoading: isArchiving }] =
    useArchivePassTemplateMutation();
  const [fetchTemplate, { data: templateDetails, isFetching: isFetchingCount }] =
    useLazyGetPassTemplateQuery();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const isConfirmed = confirmText === 'delete';

  const activePassesCount =
    !isDraft && templateDetails
      ? Number(
          (templateDetails.data.attributes as { passesCount?: number })
            .passesCount ?? 0
        )
      : 0;

  const closeDeleteModal = () => {
    setDeleteOpen(false);
    setConfirmText('');
  };

  const openDeleteModal = () => {
    setConfirmText('');
    setDeleteOpen(true);
    if (!isDraft) {
      fetchTemplate({ id: previewData.uuid, includePassesCount: true });
    }
  };

  const handleDelete = async () => {
    try {
      if (isDraft) {
        await deletePassTemplate({ id: previewData.uuid }).unwrap();
      } else {
        await archivePassTemplate({ id: previewData.uuid }).unwrap();
      }
      closeDeleteModal();
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="success"
            title={isDraft ? 'Draft template deleted' : 'Template deleted'}
            onClose={onClose}
          />
        ),
      });
    } catch (err) {
      console.error(err);
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="error"
            title={
              isDraft
                ? 'Failed to delete draft'
                : 'Failed to delete template'
            }
            onClose={onClose}
          />
        ),
      });
    }
  };

  const publishedDescription = (
    <>
      <Text textStyle="bodyFamilyRegular" fontSize="16px">
        Your template will be{' '}
        <Text as="span" fontWeight="bold">
          permanently deleted and can&apos;t be recovered.
        </Text>
      </Text>
      <Text textStyle="bodyFamilyRegular" fontSize="16px">
        All passes will be{' '}
        <Text as="span" fontWeight="bold">
          nullified
        </Text>
        . Learn about{' '}
        <Link
          color="linkBlue"
          href="https://docs.passentry.com/documentation/passes/nullification/guide"
          target="_blank"
        >
          pass nullification
        </Link>
        .
      </Text>
      {isFetchingCount ? null : activePassesCount > 0 ? (
        <Flex
          p={3}
          bg="#FEE2E2"
          borderRadius="6px"
          alignItems="center"
          gap={2}
        >
          <WarningTwoIcon color="dangerRed" boxSize={5} />
          <Text fontSize="14px">
            This template has{' '}
            <Text as="span" fontWeight="bold">
              {activePassesCount.toLocaleString()}{' '}
              {activePassesCount === 1 ? 'pass' : 'passes'}
            </Text>
            .
          </Text>
        </Flex>
      ) : null}
      <Flex direction="column" gap={2}>
        <Text fontSize="14px">
          To confirm, type{' '}
          <Text as="span" fontWeight="bold">
            delete
          </Text>{' '}
          below.
        </Text>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="delete"
          aria-label="Type delete to confirm"
        />
      </Flex>
    </>
  );

  return (
    <>
      <Menu placement="bottom-end" strategy="fixed">
        <MenuButton
          as={IconButton}
          aria-label="Template options"
          variant="ghost"
          size="sm"
          icon={
            <Text fontSize="22px" lineHeight="1" color="#4C4C4C">
              ⋯
            </Text>
          }
        />
        <Portal>
          <MenuList minW="160px">
            <MenuItem
              as={RouterLink}
              to={previewData.editUrl}
              icon={<EditIcon />}
            >
              Edit
            </MenuItem>
            <MenuItem
              icon={<CopyIcon />}
              onClick={handleDuplicate}
              isDisabled={loading || isReadOnly}
            >
              Duplicate
            </MenuItem>
            <MenuItem
              icon={<DeleteIcon />}
              color="red.500"
              onClick={openDeleteModal}
              isDisabled={isReadOnly}
            >
              Delete
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>
      <ConfirmModal
        isOpen={deleteOpen}
        onClose={closeDeleteModal}
        title={isDraft ? 'Delete this draft?' : 'Delete this template?'}
        description={
          isDraft
            ? "This draft will be permanently removed. This action can't be undone."
            : undefined
        }
        primaryButton={{
          label: isDraft ? 'Delete draft' : 'Delete template',
          variant: 'danger',
          onClick: handleDelete,
          isLoading: isDeleting || isArchiving,
          isDisabled: !isDraft && !isConfirmed,
          linkProps: {
            _disabled: {
              bg: 'gray.300',
              color: 'white',
              cursor: 'not-allowed',
              _hover: { bg: 'gray.300' },
            },
          },
        }}
        secondaryButton={{
          label: 'Cancel',
          onClick: closeDeleteModal,
        }}
      >
        {isDraft ? null : publishedDescription}
      </ConfirmModal>
    </>
  );
};

export default TemplateCardMenu;
